import { emailDomain, websiteDomain } from './normalise';
import { validatePhone } from '../validators';
import { FREE_MAIL_DOMAINS, PEOPLE_DATA_VENDOR_NAME, type EnrichmentAdapterKey, type EnrichmentAnswer, type EnrichmentDetail, type EnrichmentField } from '../ai/enrichmentSeam';

/**
 * The validation study (piece 13, *Suggestions and the Check act*; decision
 * 5382; T2738). A person's name, email, phone, employer and title, and a
 * company's name, website, main phone, address, EIN and DUNS number, are each
 * checked against what the enrichment seam finds, present or not, and each
 * gets one mark. A Differs mark carries a suggestion with its source and a
 * confidence read from the sources. 🔨 Nothing here writes a record field:
 * the marks are `RecordCheck` rows, and only a person's press writes.
 */

export const RECORD_CHECK_TYPES = ['PersonRecord', 'CompanyRecord'] as const;
export type RecordCheckRecordType = (typeof RECORD_CHECK_TYPES)[number];

/** 🔒 A person's five fields; every one but the name is checked per active employment. */
export const PERSON_CHECK_FIELDS = ['PersonName', 'Email', 'Phone', 'Employer', 'Title'] as const;
/** 🔒 A company's six fields. */
export const COMPANY_CHECK_FIELDS = ['CompanyName', 'Website', 'MainPhone', 'Address', 'Ein', 'Duns'] as const;
export type PersonCheckField = (typeof PERSON_CHECK_FIELDS)[number];
export type CompanyCheckField = (typeof COMPANY_CHECK_FIELDS)[number];
/**
 * 🔒 Decision 6180: the licensed answer read whole. A person's three list
 * fields, one row per entry, and the person's LinkedIn page with the six values
 * the vendor assumes; a company's LinkedIn page, industry, size and founding
 * year, suggested by its own check and by a person's check of its employee.
 */
export const PERSON_LIST_CHECK_FIELDS = ['JobHistory', 'Education', 'Skill'] as const;
export const PERSON_PROFILE_CHECK_FIELDS = ['LinkedInPage', 'Seniority', 'JobRole', 'JobSubRole', 'YearsExperience', 'SalaryBand', 'EmployerIndustry'] as const;
export const COMPANY_DETAIL_CHECK_FIELDS = ['LinkedInPage', 'Industry', 'CompanySize', 'FoundedYear'] as const;
export type PersonListCheckField = (typeof PERSON_LIST_CHECK_FIELDS)[number];
export type PersonProfileCheckField = (typeof PERSON_PROFILE_CHECK_FIELDS)[number];
export type CompanyDetailCheckField = (typeof COMPANY_DETAIL_CHECK_FIELDS)[number];
export type RecordCheckField = PersonCheckField | CompanyCheckField | PersonListCheckField | PersonProfileCheckField | CompanyDetailCheckField;
/** 🔒 The six values the vendor assumes about a person (decision 6180), each drawn with an Assumed label wherever it reads. */
export const ASSUMED_CHECK_FIELDS: readonly RecordCheckField[] = ['Seniority', 'JobRole', 'JobSubRole', 'YearsExperience', 'SalaryBand', 'EmployerIndustry'];
/** The person fields a row names its employment on. */
export const EMPLOYMENT_CHECK_FIELDS: readonly RecordCheckField[] = ['Email', 'Phone', 'Employer', 'Title'];
/** 🔒 The two numbers held sealed (decision 3082): suggested only where the licensed provider states one, never drawn as digits. */
export const SEALED_CHECK_FIELDS: readonly RecordCheckField[] = ['Ein', 'Duns'];

export type RecordCheckMark = 'Confirmed' | 'Differs' | 'Unknown';
/** Decision 6900: `CheckFailed` is a check that threw, so a broken lookup never reads as an empty one. */
export type RecordCheckUnknownReason = 'NoSource' | 'NoAdapter' | 'CapReached' | 'CheckFailed';
export type RecordCheckConfidence = 'High' | 'Medium' | 'Low';
export type RecordCheckRequestedBy = 'Import' | 'Press' | 'Sweep';
export type RecordCheckDecision = 'Accepted' | 'Dismissed';

export const CONFIDENCE_RANK: Readonly<Record<RecordCheckConfidence, number>> = { High: 3, Medium: 2, Low: 1 };

/** Each field's label, as the tile and the compare draw it. */
export const RECORD_CHECK_FIELD_LABELS: Readonly<Record<RecordCheckField, string>> = {
  PersonName: 'Name',
  Email: 'Email',
  Phone: 'Phone',
  Employer: 'Employer',
  Title: 'Title',
  CompanyName: 'Name',
  Website: 'Website',
  MainPhone: 'Main Phone',
  Address: 'Address',
  Ein: 'Tax ID',
  Duns: 'DUNS Number',
  JobHistory: 'Job History',
  Education: 'Education',
  Skill: 'Skill',
  LinkedInPage: 'LinkedIn Page',
  Seniority: 'Seniority',
  JobRole: 'Role',
  JobSubRole: 'Sub-Role',
  YearsExperience: 'Years of Experience',
  SalaryBand: 'Salary Band',
  EmployerIndustry: 'Employer Industry',
  Industry: 'Industry',
  CompanySize: 'Size',
  FoundedYear: 'Founded',
};

/** 🔒 The sentences of piece 13, each the whole of what the reader is told. */
export const CHECK_ALREADY_RUNNING = 'A check of this record is already running.';
export const CHECK_NO_ADAPTER = 'No outside lookup is switched on, so nothing can be checked; an administrator turns one on in Settings › Integrations.';
export const CHECK_CAP_REACHED = "This month's spending cap for outside lookups is reached.";
/** 🔒 The administrator's reading adds where the cap is raised. */
export const CHECK_CAP_REACHED_ADMIN = 'The cap is raised in Settings › Integrations › Anthropic.';
export const CHECK_OWN_COMPANY_ONLY = "A person employed only at our own Company is never checked, because their facts are HR's.";
/** The sealed suggestion's whole drawing (decision 3082). */
export const SEALED_SUGGESTION_TEXT = 'A licensed source states one';
/** Decision 6180, found in review: a job at a company not on file makes a Company, which asks for Company write beside the Person write. */
export const JOB_HISTORY_NEEDS_COMPANY_WRITE = 'This job is at a company not on file, and adding a Company needs write on Companies, which you do not hold; ask someone who does.';
/** Decision 5630: an Accept on a field that moved since its check is refused, as a stale save is. */
export const CHECK_FIELD_MOVED = 'This field changed since it was checked, so the suggestion was not written; press Check again.';

/** 🔒 The route of piece 13: the record's checks, read by the tile and written by the Check act. */
export function recordChecksPath(recordType: RecordCheckRecordType, recordId: string): string {
  return `/records/${recordType}/${encodeURIComponent(recordId)}/checks`;
}

/** One suggestion's Accept, Dismiss or, on a close employer, Same Company As (decision 7150), under its record. */
export function recordCheckActPath(recordType: RecordCheckRecordType, recordId: string, checkId: string, act: 'accept' | 'dismiss' | 'other-name'): string {
  return `${recordChecksPath(recordType, recordId)}/${encodeURIComponent(checkId)}/${act}`;
}

/** 🔒 The Records Needing Data Review page (piece 12's `queue:data-quality`) and its read. */
export const DATA_QUALITY_ROUTE = '/data-quality';
export const DATA_QUALITY_PATH = '/record-checks/review';

/** Why the Check act is shut, or null where it is offered and open. */
export type CheckActShut = 'running' | 'noAdapter' | 'capReached';

/** 🔒 What the Check act and the Suggestions tile read. */
export interface RecordChecksView {
  /** Whether the record offers the act at all: a viewer with write on its module, and never a person employed only here. */
  offered: boolean;
  shut: CheckActShut | null;
  /** The sentence the shut act reads, the administrator's line added at the cap. */
  shutSentence: string | null;
  /** Whether a check of this record is waiting on the tick. */
  waiting: boolean;
  lastCheckedAt: string | null;
  confirmed: number;
  unknown: number;
  /** 🔒 Decision 6900: the latest check's Unknown fields, each with its reason, drawn read-only under the count. */
  unknownRows: RecordUnknownRow[];
  rows: RecordSuggestionRow[];
}

/** 🔒 One Unknown mark of the latest check (decision 6900): nothing to accept, only what was not found and why. */
export interface RecordUnknownRow {
  field: RecordCheckField;
  /** The employment a person's field was checked on, named by its company. */
  employmentLabel: string | null;
  reason: RecordCheckUnknownReason;
}

/** 🔒 One undecided Differs mark on the tile. */
export interface RecordSuggestionRow {
  id: string;
  field: RecordCheckField;
  /** The employment a person's field was checked on, named by its company. */
  employmentId: string | null;
  employmentLabel: string | null;
  /** The value here, or null for none here; an EIN or a DUNS number is never sent. */
  valueHere: string | null;
  /** The suggestion; a sealed suggestion reads its sentence and never its digits. */
  suggested: string;
  sealed: boolean;
  confidence: RecordCheckConfidence;
  source: string;
  /** Linked only where it is an HTTPS or HTTP address. */
  sourceUrl: string | null;
  checkedAt: string;
  /** Whether this viewer's Accept is offered: a sealed number only to a viewer the Company edit lets write it. */
  accepts: boolean;
  /** An employer or an address opens a window prefilled, and nothing is written until its Save. */
  opensWindow: boolean;
  /** 🔒 Decision 6180: the parts a list entry or an address carries, which the tile draws beneath the suggestion. */
  detail: EnrichmentDetail | null;
  /** 🔒 Decision 6180: a value the vendor assumes, drawn with its Assumed pill. */
  assumed: boolean;
  /** 🔒 Decision 6180: the row already held that this entry folds into, named; the row then offers Merge in place of Accept. */
  merges: string | null;
  /** 🔒 Decision 6440: on a Job History row that folds into an employment, what Keep Dates and Modify Dates would each leave it reading; null on every other row. */
  jobMerge: JobMergePlan | null;
  /** 🔒 Decision 7330: on a Job History row, what its button does (adds a title, adds an employment, or a retirement), with the label the card names; null on every other row. */
  jobEntry: JobEntryPlan | null;
  /** 🔒 Decision 6444: on a Job History row whose employment moved to another company, the employments Re-pin offers; null on every other row. */
  repins: JobRepin[] | null;
  /**
   * 🔒 Decision 7150: the company a close employer is the same company as, named; on a Job History row the
   * company the job is held at, on an Employer row the employment's company. Null where the match is exact or none.
   */
  sameCompanyAs: string | null;
  /** 🔒 Decision 7150: whether this viewer is offered Same Company As, an Employer row close to its employment's company, beside Company write. */
  offersOtherName: boolean;
  /** 🔒 Decision 7360: whether this viewer is offered Add as a New Site, a company's Address row beside Company write. */
  offersNewSite: boolean;
  /** 🔒 Decision 7360: whether the address compared, the value here of an Address row, is a post office box. */
  postOfficeBox: boolean;
}

/** 🔒 Decision 7150: what Same Company As answers, the row Confirmed and the sentence naming the company and the name. */
export interface OtherNameAnswer {
  id: string;
  mark: 'Confirmed';
  companyName: string;
  name: string;
  /** Every open employer suggestion at that company the name settled, this row among them. */
  settled: number;
}

/** 🔒 Decision 6444: one employment Re-pin offers, with what Merge would then write. */
export interface JobRepin {
  employmentId: string;
  /** The employment's line as it stands now. */
  line: string;
  /** Whether this is the pinned employment as it stands, now at another company. */
  moved: boolean;
  plan: JobMergePlan;
}

/**
 * 🔒 What a Job History Merge would write (decision 6440): the employment as
 * it stands, which Keep Dates leaves it, and what Modify Dates makes it, with
 * the entry's months Modify Dates leaves alone because another employment at
 * that company holds them or because an ended employment is never reopened.
 */
export interface JobMergePlan {
  /** The employment's line as it stands now. */
  keeps: string;
  /** Its line once Modify Dates writes, or null where Modify Dates would write nothing. */
  modifies: string | null;
  /** The entry's months Modify Dates leaves alone, in words, or null. */
  leftAlone: string | null;
  /** 🔒 Decision 6442: the day Modify Dates ends an open employment on, which the person confirms first; null where it ends none. */
  ends: string | null;
  /** The company the employment is at, which the confirm window names. */
  company: string;
}

/** 🔒 Decision 6440: a person's choice on a Job History Merge. */
export type JobMergeDates = 'Keep' | 'Modify';

/**
 * 🔒 Decision 7330: what a Job History entry does. A Title adds a dated title
 * under the employment its months fall in; an Employment adds a separate
 * employment at the company for months no employment there holds; a Retired
 * entry is never a job and only offers to end the employment it names.
 */
export type JobEntryKind = 'Title' | 'Employment' | 'Retired';

/** 🔒 Decision 7330: the kind and the words the card names, an employment's line, the new employment's months or the retirement month. */
export interface JobEntryPlan {
  kind: JobEntryKind;
  label: string;
}

/**
 * 🔒 Decision 7330: whether a title reads as a retirement, Retired, Retiree or
 * Retirement in any case, alone or followed by the employer (`Retired at Baltec`),
 * and never a job. A title naming a role after the word (`Retired Engineer`) is a job.
 */
export function isRetirementTitle(title: string | null | undefined): boolean {
  return /^\s*(retired|retiree|retirement)(\s*[-–—,]?\s*(at|from|@)\s+.+)?[\s.]*$/i.test(title ?? '');
}

/** One record on the Records Needing Data Review page. */
export interface DataReviewRow {
  recordType: RecordCheckRecordType;
  recordId: string;
  label: string;
  suggestions: number;
  newestAt: string;
}

/** 🔨 Only an HTTPS or HTTP address is linked; anything else is plain text. */
export function linkableSource(url: string | null | undefined): string | null {
  if (!url) return null;
  return /^https?:\/\/[^\s]+$/i.test(url.trim()) ? url.trim() : null;
}

/** The month's spend reaching the cap. */
export function capReached(spentCents: number, capCents: number): boolean {
  return spentCents >= capCents;
}

/**
 * 🔒 Two values agree where they are equal once trimmed (piece 13). A phone is
 * compared as its E.164 number and an email without regard to case, so a name
 * differing only in case is Differs. Decision 5630: a website is compared as
 * its domain, and an address as its one line folded of case, punctuation and
 * spacing.
 */
export function checkComparand(field: RecordCheckField, value: string | null | undefined): string {
  const text = (value ?? '').trim();
  if (text === '') return '';
  if (field === 'Email') return text.toLowerCase();
  if (field === 'Phone' || field === 'MainPhone') {
    const outcome = validatePhone(text, 'US');
    return outcome.ok ? outcome.normalized : text.replace(/\D+/g, '');
  }
  if (field === 'Website') return websiteDomain(text) ?? text.toLowerCase();
  if (field === 'Address') return text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  if (field === 'Ein' || field === 'Duns') return text.replace(/\D+/g, '');
  // Decision 6180: a LinkedIn page is compared as its host and path, a count or a year as its digits.
  if (field === 'LinkedInPage') return text.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
  if (field === 'YearsExperience' || field === 'FoundedYear') return text.replace(/\D+/g, '');
  return text;
}

/** 🔒 Decision 7360: whether an address is a post office box, P.O. Box, PO Box or Post Office Box in any case and punctuation. */
export function isPostOfficeBox(address: string | null | undefined): boolean {
  return /(^| )(p ?o ?box|post office box)( |$)/.test((address ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' '));
}

export function sameCheckValue(field: RecordCheckField, left: string | null | undefined, right: string | null | undefined): boolean {
  return checkComparand(field, left) === checkComparand(field, right);
}

/** What a found value was read from: the adapter, the source line and the address, where it has one. */
export interface CheckCandidate {
  value: string;
  adapter: EnrichmentAdapterKey;
  source: string;
  /** The parts an address carries, its suite among them (decision 6180). */
  detail?: EnrichmentDetail;
  /** A confidence the rule states for this candidate's own kind, as a website taken from an email domain is Medium. */
  confidence?: RecordCheckConfidence;
}

/** A group's confidence: two adapters agreeing is High, a stated one stands next, and the adapter's own standing last. */
export function groupConfidence(group: readonly CheckCandidate[]): RecordCheckConfidence {
  const adapters = new Set(group.map((c) => c.adapter));
  if (adapters.size >= 2) return 'High';
  const stated = group.map((c) => c.confidence).filter((c): c is RecordCheckConfidence => c !== undefined);
  if (stated.length > 0) return stated.sort((a, b) => CONFIDENCE_RANK[b] - CONFIDENCE_RANK[a])[0]!;
  return confidenceOf([...adapters]);
}

/** 🔒 The finding fields a person's check reads, by the field they mark. */
export const PERSON_FINDING_TO_FIELD: Readonly<Partial<Record<EnrichmentField, PersonCheckField>>> = {
  fullName: 'PersonName',
  email: 'Email',
  phone: 'Phone',
  companyName: 'Employer',
  title: 'Title',
};
export const COMPANY_FINDING_TO_FIELD: Readonly<Partial<Record<EnrichmentField, CompanyCheckField>>> = {
  name: 'CompanyName',
  website: 'Website',
  phone: 'MainPhone',
  address: 'Address',
};
/** 🔒 Decision 6180: the person's LinkedIn page and the six assumed values, by the field they mark. */
export const PROFILE_FINDING_TO_FIELD: Readonly<Partial<Record<EnrichmentField, PersonProfileCheckField>>> = {
  linkedIn: 'LinkedInPage',
  seniority: 'Seniority',
  jobRole: 'JobRole',
  jobSubRole: 'JobSubRole',
  yearsExperience: 'YearsExperience',
  salaryBand: 'SalaryBand',
  industry: 'EmployerIndustry',
};
/** 🔒 Decision 6180: a person's answer's current employer, marked on that employer's own Company record. */
export const EMPLOYER_FINDING_TO_FIELD: Readonly<Partial<Record<EnrichmentField, CompanyCheckField | CompanyDetailCheckField>>> = {
  employerWebsite: 'Website',
  employerAddress: 'Address',
  employerLinkedIn: 'LinkedInPage',
  employerIndustry: 'Industry',
  employerSize: 'CompanySize',
  employerFounded: 'FoundedYear',
};
/** 🔒 Decision 6180: a company's own answer's detail. */
export const COMPANY_DETAIL_FINDING_TO_FIELD: Readonly<Partial<Record<EnrichmentField, CompanyDetailCheckField>>> = {
  linkedIn: 'LinkedInPage',
  industry: 'Industry',
  size: 'CompanySize',
  founded: 'FoundedYear',
};

const ADAPTER_SOURCE: Readonly<Record<EnrichmentAdapterKey, string>> = {
  peopleData: PEOPLE_DATA_VENDOR_NAME,
  website: "The company's own website",
  search: 'A web search',
};

/**
 * 🔒 The confidence is read from the sources, never from the answer's words
 * (piece 13). High is the company's own website on the record's domain, or two
 * sources agreeing; Medium the licensed provider alone; Low a search alone.
 */
export function confidenceOf(adapters: readonly EnrichmentAdapterKey[]): RecordCheckConfidence {
  const distinct = new Set(adapters);
  if (distinct.has('website') || distinct.size >= 2) return 'High';
  if (distinct.has('peopleData')) return 'Medium';
  return 'Low';
}

/** A mark as the study writes it. */
export interface CheckMark {
  mark: RecordCheckMark;
  unknownReason: RecordCheckUnknownReason | null;
  suggestedValue: string | null;
  confidence: RecordCheckConfidence | null;
  source: string | null;
  sourceUrl: string | null;
  /** The parts a suggested address or list entry carries (decision 6180). */
  detail?: EnrichmentDetail | null;
}

/** Why an answer that found nothing for a field found nothing. */
export function unknownReasonOf(answers: readonly Pick<EnrichmentAnswer, 'refusals' | 'findings'>[]): RecordCheckUnknownReason {
  const refusals = answers.flatMap((answer) => answer.refusals);
  if (refusals.some((r) => r.refusal === 'capReached' || r.refusal === 'callCeilingReached')) return 'CapReached';
  const anyFinding = answers.some((answer) => answer.findings.length > 0);
  if (!anyFinding && (refusals.length === 0 || refusals.every((r) => r.refusal === 'inactive'))) return 'NoAdapter';
  return 'NoSource';
}

/**
 * 🔒 One field's mark: the candidates grouped by value, the best supported
 * value suggested with the confidence its sources earn. A blank field with a
 * value found is Differs; a found value equal to the one here is Confirmed;
 * nothing found is Unknown with its reason.
 */
export function markOf(field: RecordCheckField, valueHere: string | null, candidates: readonly CheckCandidate[], reason: RecordCheckUnknownReason): CheckMark {
  if (candidates.length === 0) return { mark: 'Unknown', unknownReason: reason, suggestedValue: null, confidence: null, source: null, sourceUrl: null };
  const groups = new Map<string, CheckCandidate[]>();
  for (const candidate of candidates) {
    const key = checkComparand(field, candidate.value);
    if (key === '') continue;
    groups.set(key, [...(groups.get(key) ?? []), candidate]);
  }
  if (groups.size === 0) return { mark: 'Unknown', unknownReason: reason, suggestedValue: null, confidence: null, source: null, sourceUrl: null };
  const ranked = [...groups.values()].map((group) => ({ group, confidence: groupConfidence(group) }))
    .sort((a, b) => CONFIDENCE_RANK[b.confidence] - CONFIDENCE_RANK[a.confidence] || b.group.length - a.group.length);
  const best = ranked[0]!;
  // The source named is the strongest one behind the value: the website's page, then the provider, then a search.
  const order: EnrichmentAdapterKey[] = ['website', 'peopleData', 'search'];
  const lead = [...best.group].sort((a, b) => order.indexOf(a.adapter) - order.indexOf(b.adapter))[0]!;
  const url = linkableSource(lead.source);
  const here = (valueHere ?? '').trim();
  if (here !== '' && sameCheckValue(field, here, lead.value)) {
    return { mark: 'Confirmed', unknownReason: null, suggestedValue: null, confidence: best.confidence, source: url ? ADAPTER_SOURCE[lead.adapter] : lead.source, sourceUrl: url };
  }
  const detail = best.group.find((candidate) => candidate.detail)?.detail;
  return { mark: 'Differs', unknownReason: null, suggestedValue: lead.value.trim(), confidence: best.confidence, source: url ? ADAPTER_SOURCE[lead.adapter] : lead.source, sourceUrl: url, ...(detail ? { detail } : {}) };
}

/** The candidates an answer gives each field, through the finding-to-field map. */
export function candidatesByField<F extends string>(answers: readonly EnrichmentAnswer[], map: Readonly<Partial<Record<EnrichmentField, F>>>): Map<F, CheckCandidate[]> {
  const out = new Map<F, CheckCandidate[]>();
  for (const answer of answers) {
    for (const finding of answer.findings) {
      const field = map[finding.field];
      if (!field) continue;
      out.set(field, [...(out.get(field) ?? []), { value: finding.value, adapter: finding.adapter, source: finding.source, ...(finding.detail ? { detail: finding.detail } : {}) }]);
    }
  }
  return out;
}

/**
 * 🔒 A website taken from an email domain (piece 13): Medium once the site
 * answers, and High once its own pages name the company. The site answered
 * where the website adapter found anything on the domain.
 */
export function websiteFromEmailDomain(domain: string | null, companyName: string, answer: EnrichmentAnswer): CheckCandidate[] {
  if (!domain || FREE_MAIL_DOMAINS.includes(domain)) return [];
  const site = answer.findings.filter((finding) => finding.adapter === 'website');
  if (site.length === 0) return [];
  const value = `https://${domain}`;
  const names = site.some((finding) => finding.field === 'name' && finding.value.trim().toLowerCase() === companyName.trim().toLowerCase());
  return [{ value, adapter: 'website', source: value, confidence: names ? 'High' : 'Medium' }];
}

/** The most common company domain among a company's people's emails, free mail left out. */
export function companyEmailDomain(emails: readonly string[]): string | null {
  const counts = new Map<string, number>();
  for (const email of emails) {
    const domain = emailDomain(email);
    if (!domain || FREE_MAIL_DOMAINS.includes(domain)) continue;
    counts.set(domain, (counts.get(domain) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? null;
}

/** A suggested full name split into the first and the last name a person here carries: the last word is the last name. */
export function splitFullName(fullName: string): { firstName: string; lastName: string } | null {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return null;
  return { firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1]! };
}

/**
 * 🔒 A bare name, which the nightly sweep checks (piece 13): a person whose
 * first name is empty or one letter, or whose name is the email's local part,
 * and a company named all in capitals or all in lower case.
 */
export function bareName(kind: 'person', name: { firstName: string; lastName: string; emails?: readonly string[] }): boolean;
export function bareName(kind: 'company', name: { displayName: string }): boolean;
export function bareName(kind: 'person' | 'company', name: { firstName?: string; lastName?: string; emails?: readonly string[]; displayName?: string }): boolean {
  if (kind === 'company') {
    const text = (name.displayName ?? '').trim();
    if (!/[a-z]/i.test(text)) return false;
    return text === text.toUpperCase() || text === text.toLowerCase();
  }
  const first = (name.firstName ?? '').trim().replace(/\.$/, '');
  if (first.length <= 1) return true;
  const full = `${first}${(name.lastName ?? '').trim()}`.toLowerCase().replace(/[^a-z0-9]/g, '');
  return (name.emails ?? []).some((email) => {
    const local = email.trim().toLowerCase().split('@')[0] ?? '';
    const bare = local.replace(/[^a-z0-9]/g, '');
    return bare !== '' && (bare === full || bare === first.toLowerCase());
  });
}

/** 🔒 A record checked within this many days is passed over by the sweep. */
export const SWEEP_RECHECK_DAYS = 90;

/** 🔒 The nightly bare-name sweep (piece 13; decision 5631): its job, its hour and its nightly limit. */
export const RECORD_CHECKS_SWEEP_JOB = 'recordChecksSweep';
export const RECORD_CHECKS_SWEEP_HOUR_KEY = 'recordChecks.sweepRunHour';
export const RECORD_CHECKS_SWEEP_DEFAULT_HOUR = 3;
export const RECORD_CHECKS_SWEEP_LIMIT_KEY = 'recordChecks.sweepLimit';
export const RECORD_CHECKS_SWEEP_DEFAULT_LIMIT = 50;
/** The limit's seeded row; the hour's is the job registry's. */
export const RECORD_CHECKS_SETTINGS: readonly { key: string; value: number }[] = [{ key: RECORD_CHECKS_SWEEP_LIMIT_KEY, value: RECORD_CHECKS_SWEEP_DEFAULT_LIMIT }];
